/**
 * Cart Edit Product - Allows editing variant options for cart line items
 * Opens a quick view modal with the product's variant picker
 * Updates the cart by removing the old variant and adding the new one
 */

const selectors = {
  cartEditBtn: '[data-cart-edit-btn]',
  cartEditModal: '[data-cart-edit-modal]',
  cartEditModalTemplate: '[data-cart-edit-modal-template]',
  cartEditModalClose: '[data-cart-edit-modal-close]',
  cartEditModalContent: '[data-cart-edit-ajax]',
  cartEditUpdateBtn: '[data-cart-edit-update]',
  apiContent: '[data-api-content]',
  variantIdInput: '[name="id"]',
  variantSelects: 'variant-selects',
  animation: '[data-animation]',
  radioInput: 'input[type="radio"]',
  selectInput: 'select',
  popoutInput: '[data-popout-input]',
};

const attributes = {
  productHandle: 'data-product-handle',
  lineItemKey: 'data-line-item-key',
  lineItemIndex: 'data-line-item-index',
  lineItemQuantity: 'data-line-item-quantity',
  currentVariantId: 'data-current-variant-id',
  productId: 'data-product-id',
  closing: 'closing',
};

const classes = {
  loading: 'is-loading',
  animated: 'is-animated',
  disabled: 'is-disabled',
};

class CartEditProduct extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector(selectors.cartEditBtn);
    if (!this.button) return;

    this.modal = null;
    this.productHandle = this.button.getAttribute(attributes.productHandle);
    this.lineItemKey = this.button.getAttribute(attributes.lineItemKey);
    this.lineItemIndex = parseInt(this.button.getAttribute(attributes.lineItemIndex), 10);
    this.lineItemQuantity = parseInt(this.button.getAttribute(attributes.lineItemQuantity), 10) || 1;
    this.currentVariantId = this.button.getAttribute(attributes.currentVariantId);
    this.a11y = window.theme.a11y;
    this.isAnimating = false;

    this.modalClose = this.modalClose.bind(this);
    this.handleUpdateClick = this.handleUpdateClick.bind(this);
  }

  connectedCallback() {
    if (!this.button) return;
    this.button.addEventListener('click', this.handleButtonClick.bind(this));
  }

  handleButtonClick(e) {
    e.preventDefault();

    this.button.classList.add(classes.loading);
    this.button.disabled = true;

    this.renderModal();
  }

  renderModal() {
    window
      .fetch(`${window.theme.routes.root}products/${this.productHandle}?section_id=api-cart-edit`)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then((response) => {
        this.modalCreate(response);
      })
      .catch((error) => {
        this.button.classList.remove(classes.loading);
        this.button.disabled = false;
      });
  }

  modalCreate(response) {
    const modalTemplate = this.querySelector(selectors.cartEditModalTemplate);
    if (!modalTemplate) return;

    const htmlObject = document.createElement('div');
    htmlObject.innerHTML = modalTemplate.innerHTML;

    const modalElement = htmlObject.querySelector(selectors.cartEditModal);

    const uniqueId = `cart-edit-modal-${Date.now()}`;
    modalElement.setAttribute('data-cart-edit-modal-id', uniqueId);

    document.body.appendChild(modalElement);

    this.modal = document.body.querySelector(`[data-cart-edit-modal-id="${uniqueId}"]`);
    
    if (!this.modal) {
      this.button.classList.remove(classes.loading);
      this.button.disabled = false;
      return;
    }
    
    const contentContainer = this.modal.querySelector(selectors.cartEditModalContent);
    const parsedResponse = new DOMParser().parseFromString(response, 'text/html');
    const apiContent = parsedResponse.querySelector(selectors.apiContent);
    
    if (apiContent) {
      contentContainer.innerHTML = apiContent.innerHTML;
    }

    this.modalCreatedCallback();
  }

  modalCreatedCallback() {
    this.modalEvents();
    this.modalOpen();
    this.setupUpdateButton();
    this.setupVariantChangeListener();

    const errorContainer = this.modal.querySelector('[data-form-errors-container]');
    if (errorContainer) {
      errorContainer.innerHTML = '';
    }
  }

  modalOpen() {
    if (typeof this.modal.show === 'function') {
      this.modal.show();
    }

    this.modal.setAttribute('open', true);
    this.modal.removeAttribute('inert');

    if (this.button) {
      this.button.classList.remove(classes.loading);
      this.button.disabled = false;
      window.a11y.lastElement = this.button;
    }

    requestAnimationFrame(() => {
      this.modal.querySelectorAll(selectors.animation).forEach((item) => {
        item.classList.add(classes.animated);
      });
    });

    document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
  }

  modalClose() {
    if (this.isAnimating) {
      return;
    }

    if (!this.modal.hasAttribute(attributes.closing)) {
      this.modal.setAttribute(attributes.closing, '');
      this.isAnimating = true;
      return;
    }

    if (typeof this.modal.close === 'function') {
      this.modal.close();
    } else {
      this.modal.removeAttribute('open');
    }

    this.modal.removeAttribute(attributes.closing);
    this.modal.setAttribute('inert', '');
    this.modal.classList.remove(classes.loading);

    if (this.button) {
      this.button.disabled = false;
    }

    this.resetAnimatedItems();

    if (this.variantChangeUnsubscribe) {
      this.variantChangeUnsubscribe();
    }

    if (!window.theme.hasOpenModals()) {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    }

    this.a11y.removeTrapFocus();
    this.a11y.autoFocusLastElement();
    this.modalRemove();
  }

  modalRemove() {
    if (!this.modal) return;
    
    window.theme
      .waitForAllAnimationsEnd(this.modal)
      .then(() => {
        if (this.modal && this.modal.parentNode) {
          this.modal.remove();
          this.modal = null;
        }
      })
      .catch(() => {
        if (this.modal && this.modal.parentNode) {
          this.modal.remove();
          this.modal = null;
        }
      });
  }

  forceCloseModal() {
    if (!this.modal) return;

    if (typeof this.modal.close === 'function') {
      this.modal.close();
    }

    this.modal.removeAttribute('open');
    this.modal.setAttribute('inert', '');
    this.modal.removeAttribute(attributes.closing);
    this.modal.classList.remove(classes.loading);
    this.isAnimating = false;

    if (this.variantChangeUnsubscribe) {
      this.variantChangeUnsubscribe();
      this.variantChangeUnsubscribe = null;
    }

    this.resetAnimatedItems();

    if (!window.theme.hasOpenModals()) {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    }

    if (this.a11y) {
      this.a11y.removeTrapFocus();
      this.a11y.autoFocusLastElement();
    }

    if (this.modal && this.modal.parentNode) {
      this.modal.remove();
      this.modal = null;
    }

    const remainingModals = document.querySelectorAll('dialog.cart-edit-modal');
    remainingModals.forEach(modal => {
      if (modal.parentNode) {
        modal.removeAttribute('open');
        modal.setAttribute('inert', '');
        modal.remove();
      }
    });
  }

  cleanupStuckModal() {
    const stuckModals = document.querySelectorAll('dialog.cart-edit-modal, dialog[data-cart-edit-modal]');
    stuckModals.forEach(modal => {
      if (modal.parentNode) {
        modal.removeAttribute('open');
        modal.setAttribute('inert', '');
        modal.style.display = 'none';
        modal.style.visibility = 'hidden';
        modal.style.pointerEvents = 'none';
        modal.remove();
      }
    });

    const overlays = document.querySelectorAll('.cart-edit-modal, [data-cart-edit-modal]');
    overlays.forEach(overlay => {
      if (overlay.style && (overlay.style.zIndex > 1000 || overlay.classList.contains('is-open'))) {
        overlay.style.display = 'none';
        overlay.style.visibility = 'hidden';
        overlay.style.pointerEvents = 'none';
        overlay.removeAttribute('open');
        overlay.setAttribute('inert', '');
      }
    });
  }

  modalEvents() {
    this.modal.querySelector(selectors.cartEditModalClose)?.addEventListener('click', (e) => {
      e.preventDefault();
      this.modalClose();
    });

    this.modal.addEventListener('click', (event) => {
      if (event.target.nodeName === 'DIALOG' && event.type === 'click') {
        this.modalClose();
      }
    });

    this.modal.addEventListener('keydown', (event) => {
      if (event.code === 'Escape') {
        event.preventDefault();
        this.modalClose();
      }
    });

    this.modal.addEventListener('transitionend', (event) => {
      if (event.target === this.modal && this.modal.hasAttribute(attributes.closing)) {
        this.isAnimating = false;
        this.modalClose();
      }
    });
  }

  setupUpdateButton() {
    const updateBtn = this.modal.querySelector(selectors.cartEditUpdateBtn);
    if (!updateBtn) return;

    updateBtn.addEventListener('click', this.handleUpdateClick);
  }

  setupVariantChangeListener() {
    const variantSelects = this.modal.querySelector(selectors.variantSelects);
    if (!variantSelects) return;

    variantSelects.addEventListener('change', () => {
      setTimeout(() => {
        this.updateVariantFromOptions();
        this.updateButtonState();
      }, 100);
    });

    if (window.subscribe && window.theme?.PUB_SUB_EVENTS?.optionValueSelectionChange) {
      this.variantChangeUnsubscribe = window.subscribe(
        window.theme.PUB_SUB_EVENTS.optionValueSelectionChange,
        (event) => {
          if (event.data?.event?.target?.closest('[data-cart-edit-modal]')) {
            setTimeout(() => {
              this.updateVariantFromOptions();
              this.updateButtonState();
            }, 100);
          }
        }
      );
    }
  }

  async updateVariantFromOptions() {
    const variantSelects = this.modal.querySelector(selectors.variantSelects);
    if (!variantSelects) return;

    const selectedRadios = Array.from(variantSelects.querySelectorAll('input[type="radio"]:checked'));
    const selectedPopouts = Array.from(variantSelects.querySelectorAll('[data-popout-input]'));

    const selectedValues = [];
    const allInputs = [...selectedRadios, ...selectedPopouts];

    allInputs.sort((a, b) => {
      const aPos = parseInt(a.name.match(/-(\d+)$/)?.[1] || '0');
      const bPos = parseInt(b.name.match(/-(\d+)$/)?.[1] || '0');
      return aPos - bPos;
    });

    selectedValues.push(...allInputs.map(input => input.value));

    if (selectedValues.length === 0) return;

    try {
      const productResponse = await fetch(`${window.theme.routes.root}products/${this.productHandle}.js`);
      if (!productResponse.ok) {
        throw new Error('Failed to fetch product');
      }
      const product = await productResponse.json();

      const matchingVariant = product.variants.find(variant => {
        if (variant.options.length !== selectedValues.length) {
          return false;
        }
        return variant.options.every((option, index) => {
          return option === selectedValues[index];
        });
      });

      if (matchingVariant) {
        const variantInput = this.modal.querySelector(selectors.variantIdInput);
        if (variantInput) {
          variantInput.value = matchingVariant.id;
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    } catch (error) {
    }
  }

  updateButtonState() {
    const updateBtn = this.modal.querySelector(selectors.cartEditUpdateBtn);
    const variantInput = this.modal.querySelector(selectors.variantIdInput);
    const errorContainer = this.modal.querySelector('[data-form-errors-container]');
    
    if (!updateBtn || !variantInput) return;

    if (errorContainer) {
      errorContainer.innerHTML = '';
    }

    const variantId = variantInput.value;
    const formWrapper = this.modal.querySelector('[data-form-wrapper]');

    const isUnavailable = formWrapper?.classList.contains('variant--soldout') || 
                          formWrapper?.classList.contains('variant--unavailable');
    
    if (variantId && !isUnavailable) {
      updateBtn.disabled = false;
      updateBtn.querySelector('[data-add-to-cart-text]').textContent = 
        window.theme?.strings?.updateItem || 'Update Item';
    } else if (isUnavailable) {
      updateBtn.disabled = true;
      updateBtn.querySelector('[data-add-to-cart-text]').textContent = 
        window.theme?.strings?.soldOut || 'Sold Out';
    } else {
      updateBtn.disabled = true;
      updateBtn.querySelector('[data-add-to-cart-text]').textContent = 
        window.theme?.strings?.unavailable || 'Unavailable';
    }
  }

  async handleUpdateClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const updateBtn = this.modal.querySelector(selectors.cartEditUpdateBtn);
    const variantInput = this.modal.querySelector(selectors.variantIdInput);
    
    if (!variantInput) return;

    await this.updateVariantFromOptions();

    const newVariantId = variantInput.value;

    if (newVariantId === this.currentVariantId) {
      this.modalClose();
      return;
    }

    updateBtn.classList.add(classes.loading);
    updateBtn.disabled = true;

    this.updateCartItem(newVariantId);
  }

  async updateCartItem(newVariantId) {
    try {
      const removeResponse = await fetch(window.theme.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          line: this.lineItemIndex,
          quantity: 0,
        }),
      });

      if (!removeResponse.ok) {
        throw new Error('Failed to remove item from cart');
      }

      const formData = new FormData();
      formData.append('id', parseInt(newVariantId, 10));
      formData.append('quantity', this.lineItemQuantity);

      const addResponse = await fetch(window.theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript',
        },
        body: formData,
      });

      if (!addResponse.ok) {
        const addErrorText = await addResponse.text();
        let errorData;
        try {
          errorData = await addResponse.json();
        } catch (e) {
          errorData = { description: addErrorText || 'Failed to add item to cart' };
        }
        throw new Error(errorData.description || 'Failed to add item to cart');
      }

      document.dispatchEvent(
        new CustomEvent('theme:cart:change', {
          bubbles: true,
          detail: {
            cartItemUpdated: true,
          },
        })
      );

      document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));

      await this.refreshCart();

      this.forceCloseModal();

      setTimeout(() => {
        this.cleanupStuckModal();
      }, 1000);

    } catch (error) {
      if (!this.modal) return;
      
      const updateBtn = this.modal.querySelector(selectors.cartEditUpdateBtn);
      const errorContainer = this.modal.querySelector('[data-form-errors-container]');
      
      if (updateBtn) {
        updateBtn.classList.remove(classes.loading);
        updateBtn.disabled = false;
      }

      if (errorContainer) {
        errorContainer.innerHTML = `<p class="form-error">${error.message}</p>`;
      }
    }
  }

  async refreshCart() {
    const cartItemsFromButton = this.button?.closest('cart-items');
    const cartItems = cartItemsFromButton || document.querySelector('cart-items');
    const cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer && typeof cartDrawer.getCart === 'function') {
      cartDrawer.getCart();
      await new Promise(resolve => setTimeout(resolve, 400));
    } else if (cartItems && typeof cartItems.getCart === 'function') {
      cartItems.getCart();
      await new Promise(resolve => setTimeout(resolve, 400));
    } else {
      if (window.location.pathname.includes('/cart')) {
        window.location.reload();
        return;
      }
      await new Promise(resolve => setTimeout(resolve, 400));
    }
  }

  resetAnimatedItems() {
    this.modal?.querySelectorAll(selectors.animation).forEach((item) => {
      item.classList.remove(classes.animated);
    });
  }
}

if (!customElements.get('cart-edit-product')) {
  customElements.define('cart-edit-product', CartEditProduct);
}
